/**
 * Stream session lifecycle hook.
 *
 * Manages the full flow from SDK initialization to active streaming:
 *
 *   1. Create GameLiftStreams SDK instance (needs <video> and <audio> elements)
 *   2. Enable microphone on the SDK (adds mic track to the WebRTC offer)
 *   3. Generate a signal request (WebRTC SDP offer, base64-encoded)
 *   4. POST /api/session/start → get session ARN
 *   5. Poll GET /api/session/:sg/:arn every 1s until status === "ACTIVE"
 *   6. Process the signal response (WebRTC SDP answer) → stream connects
 *   7. Attach input (keyboard, mouse, gamepad, mic forwarding begins)
 *
 * IMPORTANT: enableMicrophone() MUST be called before generateSignalRequest().
 * The mic track needs to be part of the WebRTC offer so the remote side
 * knows to expect audio input. If you generate the offer first and add the
 * mic later, the remote side won't set up an audio receiver.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import {
  GameLiftStreams,
  type PerformanceStats,
} from "../lib/gamelift-streams-websdk/gameliftstreams-1.1.0.mjs";
import * as api from "../lib/api";
import type { LatencyResult } from "../lib/latency";
import type { CapacityStatus } from "@shared/types";
import { isMobile } from "../lib/isMobile";

/** How often to poll for session status (milliseconds) */
const POLL_INTERVAL_MS = 1000;
/** Maximum time to wait for a session to become ACTIVE (milliseconds) */
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

interface UseStreamSessionResult {
  status: string;
  statusReason: string;
  capacityStatus: CapacityStatus | null;
  sessionLocation: string;
  glsInstance: GameLiftStreams | null;
  sessionArn: string;
  perfStats: PerformanceStats | null;
  startSession: (deviceId: string, latencies: LatencyResult[], userId: string, turnstileToken: string) => Promise<void>;
  endSession: () => void;
}

export function useStreamSession(gameSlug: string): UseStreamSessionResult {
  const [status, setStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [capacityStatus, setCapacityStatus] = useState<CapacityStatus | null>(null);
  const sessionArnRef = useRef("");
  const [sessionLocation, setSessionLocation] = useState("");
  const [perfStats, setPerfStats] = useState<PerformanceStats | null>(null);
  const glsRef = useRef<GameLiftStreams | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionEndedRef = useRef(true); // When true, in-flight start/poll requests must not continue or be rescheduled

  const endSession = useCallback(() => {
    sessionEndedRef.current = true;

    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    if (glsRef.current) {
      glsRef.current.close();
      glsRef.current = null;
    }

    if (sessionArnRef.current) {
      api.terminateSession(sessionArnRef.current).catch((err) =>
        console.error("Failed to terminate session:", err)
      );
      sessionArnRef.current = "";
    }

    setStatus("");
    setStatusReason("");
    setCapacityStatus(null);
    setSessionLocation("");
    setPerfStats(null);
  }, []);

  // Terminate the session if the user closes/navigates away from the page.
  useEffect(() => {
    const handleUnload = () => {
      const arn = sessionArnRef.current;
      if (arn) {
        navigator.sendBeacon(
          "/api/session/terminate",
          new Blob([JSON.stringify({ sessionArn: arn })], { type: "application/json" })
        );
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      endSession();
    };
  }, [endSession]);

  const startSession = useCallback(
    async (deviceId: string, latencies: LatencyResult[], userId: string, turnstileToken: string) => {
      endSession();
      sessionEndedRef.current = false;

      try {
        const videoEl = document.getElementById("stream-video") as HTMLVideoElement;
        const audioEl = document.getElementById("stream-audio") as HTMLAudioElement;

        if (!videoEl || !audioEl) {
          throw new Error("Video/audio elements not found. Ensure StreamView is mounted.");
        }

        const gls = new GameLiftStreams({
          videoElement: videoEl,
          audioElement: audioEl,
          inputConfiguration: {
            autoPointerLock: isMobile() ? false : true,
            setCursor: !isMobile(),
            autoMouse: !isMobile(),
            trackWindowFocus: !isMobile(), // Handle blur/focus manually to avoid inputs getting stuck when lose focus
            // Intercept F11 so it toggles browser fullscreen instead of being
            // forwarded to the game. Return false to suppress forwarding.
            keyboardFilter: (event: KeyboardEvent) => {
              if (event.key === "F11") {
                event.preventDefault();
                if (event.type === "keydown") {
                  const container = document.querySelector(".stream-view");
                  if (document.fullscreenElement) {
                    document.exitFullscreen().catch(() => {});
                  } else {
                    container?.requestFullscreen().catch(() => {});
                  }
                }
                return false;
              }
              return true;
            },
          },
          clientConnection: {
            connectionState: (state: string) => {
              console.log("WebRTC connection state:", state);
            },
            channelError: (e: any) => {
              if (sessionEndedRef.current) return;
              console.error("WebRTC channel error:", e);
              setStatus("ERROR");
            },
            serverDisconnect: (reason: string) => {
              if (sessionEndedRef.current) return;
              console.log("Server disconnect:", reason);
              setStatus("DISCONNECTED");
            },
            performanceStats: (stats: PerformanceStats) => {
              if (sessionEndedRef.current) return;
              setPerfStats(stats);
            },
          },
        });
        glsRef.current = gls;

        await gls.enableMicrophone({
          deviceId: { exact: deviceId },
          noiseSuppression: false,
          echoCancellation: false,
          autoGainControl: false,
        });
        if (sessionEndedRef.current) return;

        const signalRequest = await gls.generateSignalRequest();
        if (sessionEndedRef.current) return;

        setStatus("PROVISIONING");
        const startResult = await api.startSession({
          gameSlug,
          signalRequest,
          latencies: latencies.map((l) => ({ region: l.region, latencyMs: l.latency })),
          isMobile: isMobile(),
          userId,
          turnstileToken,
        });
        if (sessionEndedRef.current) {
          if (startResult.capacityStatus !== "full" && startResult.arn) {
            api.terminateSession(startResult.arn).catch((err) =>
              console.error("Failed to terminate session:", err)
            );
          }
          return;
        }

        setCapacityStatus(startResult.capacityStatus);
        setStatusReason(startResult.statusReason);

        if (startResult.capacityStatus === "full") {
          setStatus("FULL");
          sessionEndedRef.current = true;
          glsRef.current?.close();
          glsRef.current = null;
          return;
        }

        sessionArnRef.current = startResult.arn;
        setSessionLocation(startResult.location);
        setStatus(startResult.status);

        // A newly started session is always PROVISIONING/ACTIVATING, never
        // immediately ACTIVE, so we always poll until it becomes ACTIVE.
        const sgId = startResult.arn.split("/")[1];
        const startTime = Date.now();

        const schedulePoll = () => {
          if (sessionEndedRef.current) return;
          pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        };

        async function poll() {
          if (sessionEndedRef.current) return;

          if (Date.now() - startTime > POLL_TIMEOUT_MS) {
            setStatusReason("The session took too long to start.");
            setStatus("ERROR");
            return;
          }

          try {
            const session = await api.getSession(sgId, startResult.arn);
            if (sessionEndedRef.current) return;

            setStatus(session.status);

            if (session.status === "ACTIVE" && session.signalResponse) {
              await gls.processSignalResponse(session.signalResponse);
              if (sessionEndedRef.current) return;

              gls.attachInput();
              setStatus("ACTIVE");
              return;
            }

            if (session.status === "ERROR" || session.status === "TERMINATED") {
              setStatusReason(session.statusReason || "The session ended unexpectedly.");
              setStatus("ERROR");
              return;
            }

            schedulePoll();
          } catch (err) {
            if (sessionEndedRef.current) return;
            console.error("Poll error:", err);
            setStatusReason("Lost connection to the server.");
            setStatus("ERROR");
          }
        }

        schedulePoll();
      } catch (err) {
        if (sessionEndedRef.current) return;
        console.error("Failed to start stream session:", err);
        setStatusReason("Failed to start the session. Please try again.");
        setStatus("ERROR");
      }
    },
    [gameSlug, endSession]
  );

  return {
    status,
    statusReason,
    capacityStatus,
    sessionLocation,
    glsInstance: glsRef.current,
    sessionArn: sessionArnRef.current,
    perfStats,
    startSession,
    endSession,
  };
}
